import apiClient from './client';

export interface User {
  admin_id: number;
  username: string;
  display_name: string;
  role: 'administrator' | 'manager';
  last_login?: string;
}

export interface LoginResponse {
  status: string;
  token: string;  // 하위 호환성
  access_token: string;
  refresh_token: string;
  expires_in: number;  // 초 단위
  user: User;
}

export interface RefreshResponse {
  status: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: User;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// 에러 코드 정의
export const AuthErrorCode = {
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_INVALID: 'TOKEN_INVALID',
  REFRESH_TOKEN_EXPIRED: 'REFRESH_TOKEN_EXPIRED',
  REFRESH_TOKEN_INVALID: 'REFRESH_TOKEN_INVALID',
  AUTHENTICATION_REQUIRED: 'AUTHENTICATION_REQUIRED',
} as const;

// 토큰 저장 키
const TOKEN_KEY = 'spojoy_auth_token';
const REFRESH_TOKEN_KEY = 'spojoy_refresh_token';
const USER_KEY = 'spojoy_user';

// 토큰 관리
export const getStoredToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

export const setStoredToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

export const getStoredRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const setStoredRefreshToken = (token: string): void => {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
};

export const removeStoredTokens = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

// 하위 호환성을 위한 alias
export const removeStoredToken = removeStoredTokens;

export const getStoredUser = (): User | null => {
  const userJson = localStorage.getItem(USER_KEY);
  if (userJson) {
    try {
      return JSON.parse(userJson);
    } catch {
      return null;
    }
  }
  return null;
};

export const setStoredUser = (user: User): void => {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

// API 호출
export const login = async (username: string, password: string): Promise<LoginResponse> => {
  const response = await apiClient.post('/api/auth/login', {
    username,
    password
  });

  // 로그인 성공 시 토큰과 사용자 정보 저장
  if (response.data.access_token) {
    setStoredToken(response.data.access_token);
    setStoredRefreshToken(response.data.refresh_token);
    setStoredUser(response.data.user);
    // API 클라이언트에 토큰 설정
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
  } else if (response.data.token) {
    // 하위 호환성: 기존 token 필드 지원
    setStoredToken(response.data.token);
    setStoredUser(response.data.user);
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
  }

  return response.data;
};

export const refreshAccessToken = async (): Promise<RefreshResponse | null> => {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    console.log('[인증] Refresh Token이 없음');
    return null;
  }

  try {
    const response = await apiClient.post('/api/auth/refresh', {
      refresh_token: refreshToken
    });

    // 새 토큰 저장
    if (response.data.access_token) {
      setStoredToken(response.data.access_token);
      setStoredRefreshToken(response.data.refresh_token);
      setStoredUser(response.data.user);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      console.log('[인증] 토큰 갱신 성공');
    }

    return response.data;
  } catch (error: any) {
    console.error('[인증] 토큰 갱신 실패:', error?.response?.data?.detail?.message || error.message);
    return null;
  }
};

export const logout = async (): Promise<void> => {
  try {
    await apiClient.post('/api/auth/logout');
  } catch (error) {
    console.error('[인증] 로그아웃 오류:', error);
  } finally {
    // 항상 로컬 데이터 삭제
    removeStoredTokens();
    delete apiClient.defaults.headers.common['Authorization'];
  }
};

// 강제 로그아웃 (토큰 만료 등)
export const forceLogout = (): void => {
  console.log('[인증] 세션 만료로 로그아웃 처리');
  removeStoredTokens();
  delete apiClient.defaults.headers.common['Authorization'];
  window.dispatchEvent(new CustomEvent('auth:session-expired'));
};

export const getCurrentUser = async (): Promise<User> => {
  const response = await apiClient.get('/api/auth/me');
  return response.data.user;
};

export const changePassword = async (oldPassword: string, newPassword: string): Promise<void> => {
  await apiClient.put('/api/auth/password', {
    old_password: oldPassword,
    new_password: newPassword
  });
};

// 관리자 관리 (administrator 전용)
export const listAdmins = async (): Promise<User[]> => {
  const response = await apiClient.get('/api/auth/admins');
  return response.data.admins;
};

export const registerAdmin = async (
  username: string,
  password: string,
  displayName: string,
  role: 'administrator' | 'manager'
): Promise<User> => {
  const response = await apiClient.post('/api/auth/register', {
    username,
    password,
    display_name: displayName,
    role
  });
  return response.data.admin;
};

export const toggleAdminStatus = async (adminId: number): Promise<boolean> => {
  const response = await apiClient.put(`/api/auth/admins/${adminId}/toggle`);
  return response.data.is_active;
};

export const updateAdmin = async (
  adminId: number,
  data: {
    username?: string;
    display_name?: string;
    role?: 'administrator' | 'manager';
    new_password?: string;
  }
): Promise<User> => {
  const response = await apiClient.put(`/api/auth/admins/${adminId}`, data);
  return response.data.admin;
};

export const deleteAdmin = async (adminId: number): Promise<void> => {
  await apiClient.delete(`/api/auth/admins/${adminId}`);
};

export const updateProfile = async (displayName: string): Promise<User> => {
  const response = await apiClient.put('/api/auth/profile', {
    display_name: displayName
  });
  return response.data.user;
};

// 초기 관리자 생성 (개발용)
export const initAdmin = async (): Promise<void> => {
  await apiClient.post('/api/auth/init');
};

// 앱 시작 시 토큰이 있으면 헤더에 설정 (동기, 빠른 초기화용)
export const initializeAuth = (): AuthState => {
  const token = getStoredToken();
  const user = getStoredUser();

  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  return {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading: false
  };
};

// 백엔드 검증을 포함한 세션 유효성 확인 (비동기)
// - 토큰 만료 시 인터셉터가 자동으로 갱신 시도
// - 갱신 실패 시 인터셉터가 forceLogout 처리 후 예외 던짐
export const validateSession = async (): Promise<AuthState> => {
  const token = getStoredToken();
  const user = getStoredUser();

  if (!token || !user) {
    return { user: null, token: null, isAuthenticated: false, isLoading: false };
  }

  apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;

  try {
    const meResponse = await apiClient.get('/api/auth/me');
    const currentUser = meResponse.data.user;
    setStoredUser(currentUser);
    return { user: currentUser, token: getStoredToken()!, isAuthenticated: true, isLoading: false };
  } catch {
    return { user: null, token: null, isAuthenticated: false, isLoading: false };
  }
};
